/**
 * Returns a prop type validator which checks whether a prop is either a component or an array of
 * components of a given type, based on their `name` or `displayName` properties.
 * Use an array of strings for `passedName` to accept more than one component type.
 * For an array the validation fails on the first occurrence of an invalid element.
 *
 * @param {string|string[]} passedName
 * @return {function}
 */
export default function componentPropType(passedName) {
  const passedNames = typeof passedName === 'string' ? [passedName] : passedName;
  const checkProp = (props, propName, componentName) => {
    if (props[propName] === undefined || props[propName] === null) {
      return undefined;
    }
    if (Array.isArray(props[propName])) {
      // Iterates through every child and try to find the first element that does not match the passed name
      // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Boolean
      return props[propName].map((_, index) => checkProp(props[propName], index, componentName)).find(Boolean);
    }
    const nameInProp = props[propName].type?.displayName || props[propName].type?.name;
    if (!nameInProp || !passedNames.includes(nameInProp)) {
      const propDescription = nameInProp ? `Component ${nameInProp}` : typeof props[propName];
      return new Error(`${componentName}: ${propDescription} was passed to \`${propName}\` prop; should be ${passedNames.join(' or ')}`);
    }
    return undefined;
  };
  const checkRequired = (props, propName, componentName) => {
    if (props[propName] === undefined) {
      return new Error(`The prop \`${propName}\` is marked as required in \`${componentName}\`, but its value is ${props[propName]}.`);
    }
    return undefined;
  };
  const createValidate = isRequired => {
    if (isRequired) {
      return (props, propName, componentName) => {
        const checkForError = checkProp(props, propName, componentName);
        if (checkForError) {
          return checkForError;
        }
        return checkRequired(props, propName, componentName);
      };
    }
    return checkProp;
  };
  const validate = createValidate();
  validate.isRequired = createValidate(true);
  return validate;
}